import React from 'react';
import { HeroSectionProps } from '../../types/components';
import { useCountUp } from '../../hooks/useCountUp';

// Icon mapping for each metric type
const getMetricIcon = (label: string) => {
  if (label.includes('Competencies')) {
    // Medal/Badge icon
    return (
      <svg className="w-8 h-8 mx-auto mb-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    );
  } else if (label.includes('Service Validations')) {
    // Shield with checkmark icon
    return (
      <svg className="w-8 h-8 mx-auto mb-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    );
  } else if (label.includes('Launches')) {
    // Rocket icon
    return (
      <svg className="w-8 h-8 mx-auto mb-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
      </svg>
    );
  } else if (label.includes('Certifications')) {
    // Diploma/Certificate icon
    return (
      <svg className="w-8 h-8 mx-auto mb-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  }
  return null;
};

// Component to handle individual metric animation
const AnimatedMetric: React.FC<{ value: string; label: string }> = ({ value, label }) => {
  // Extract numeric value and suffix (like + or K)
  const numericMatch = value.match(/^(\d+)/);
  const numericValue = numericMatch ? parseInt(numericMatch[1], 10) : 0;
  const suffix = value.replace(/^\d+/, '');
  
  const count = useCountUp({ 
    end: numericValue, 
    duration: 2000
  });

  return (
    <div className="text-center">
      {getMetricIcon(label)}
      <div className="text-3xl lg:text-4xl font-bold text-blue-600 mb-2">
        {count}{suffix}
      </div>
      <div className="text-gray-700 font-medium text-lg">
        {label}
      </div>
    </div>
  );
};

const HeroSection: React.FC<HeroSectionProps> = ({
  headline,
  subHeadline,
  ctaButton,
  credibilityMetrics
}) => {
  return (
    <section className="hero-intense py-16 lg:py-24 overflow-hidden">
      <style>{`
        @keyframes wave1 {
          0%, 100% {
            background-position: 0% 20%, 100% 100%, 0% 0%, 30% 100%;
          }
          33% {
            background-position: 40% 30%, 80% 90%, 20% 10%, 50% 80%;
          }
          66% {
            background-position: 80% 40%, 60% 80%, 10% 30%, 70% 90%;
          }
        }
        
        @keyframes wave2 {
          0%, 100% {
            opacity: 1;
          }
          25% {
            opacity: 0.88;
          }
          50% {
            opacity: 0.92;
          }
          75% {
            opacity: 0.85;
          }
        }
        
        @keyframes wave3 {
          0% {
            filter: hue-rotate(0deg) brightness(1);
          }
          50% {
            filter: hue-rotate(3deg) brightness(1.05);
          }
          100% {
            filter: hue-rotate(0deg) brightness(1);
          }
        }
        
        .hero-intense {
          /* Fallback Background */
          background-color: var(--hcl-tech-purple);
          /* Layered Mesh Gradient */
          background-image: 
            /* 1. Top Right Corner: Mid Blue fading into Tech Blue */
            radial-gradient(circle at 100% 0%, var(--hcl-mid-blue) 0%, var(--hcl-tech-blue) 30%, transparent 60%),
            /* 2. Bottom Right Corner: Dark Blue anchor */
            radial-gradient(circle at 100% 100%, var(--hcl-dark-blue) 0%, transparent 50%),
            /* 3. Top Left Corner: Mid Purple highlight */
            radial-gradient(circle at 0% 0%, var(--hcl-mid-purple) 0%, transparent 50%),
            /* 4. Bottom Left & Center: Tech Purple Base */
            radial-gradient(circle at 30% 100%, var(--hcl-tech-purple) 0%, transparent 60%);
          background-size: 250% 250%;
          animation: wave1 15s ease-in-out infinite, wave2 7s ease-in-out infinite, wave3 12s ease-in-out infinite;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        
        .animate-fadeInScale {
          animation: fadeInScale 0.6s ease-out forwards;
        }
        
        .animation-delay-200 {
          animation-delay: 0.2s;
          opacity: 0;
        }
        
        .animation-delay-400 {
          animation-delay: 0.4s;
          opacity: 0;
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Content */}
        <div className="text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fadeInUp">
            {headline}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-4xl mx-auto leading-relaxed animate-fadeInUp animation-delay-200">
            {subHeadline}
          </p>
          
          {/* Primary CTA Button */}
          <div className="mb-16 animate-fadeInScale animation-delay-400">
            <a
              href={ctaButton.href}
              className={`inline-flex items-center px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-offset-2 ${
                ctaButton.variant === 'primary'
                  ? 'bg-white text-blue-600 hover:bg-gray-50 focus:ring-white shadow-lg hover:shadow-xl'
                  : 'bg-white text-blue-600 border-2 border-white hover:bg-gray-50 focus:ring-white shadow-md hover:shadow-lg'
              }`}
            >
              {ctaButton.label}
              <svg
                className="ml-2 h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </a>
          </div>
        </div>

        {/* Credibility Bar */}
        <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
            {credibilityMetrics.map((metric, index) => (
              <AnimatedMetric
                key={`${metric.value}-${metric.label}`}
                value={metric.value}
                label={metric.label}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;